/** Clip drafts on the Watch page (pure model). Drafts are work in progress, kept per video in sessionStorage so a
 * reload does not lose them; "Create clips" turns them into one batch POST. */
import { nextOnGrid, stepFrames } from "../../lib/frames";
import type { BoardPage, ClipIn, MvClipOut, Suggestion, Timeline } from "../types";

/** Who made a clip window: picked from a suggestion, set by hand with In/Out (`manual`), or by a bot (`agent`). */
export type DraftOrigin = "suggested" | "manual" | "agent";
export type Draft = { key: string; inMs: number; outMs: number; label: string; origin: DraftOrigin;
  score: number | null; reason: string | null };

/** The origin of a clip made from In/Out: `agent` when this tab is an agent token (`/api/me` kind), else `manual`
 * (the server applies the hand-made length range only to `manual`). */
export function inOutOrigin(kind: string | null | undefined): "manual" | "agent" {
  return kind === "agent" ? "agent" : "manual";
}
export type Win = { inMs: number; outMs: number };

export const winKey = (inMs: number, outMs: number) => `${inMs}-${outMs}`;

export function fromSuggestion(s: Suggestion): Draft {
  return { key: winKey(s.t_mv_in_ms, s.t_mv_out_ms), inMs: s.t_mv_in_ms, outMs: s.t_mv_out_ms, label: s.label,
    origin: "suggested", score: s.score, reason: s.reasons.join(", ") || null };
}

export function manualDraft(inMs: number, outMs: number, n: number, origin: "manual" | "agent" = "manual"): Draft {
  return { key: `m${Date.now().toString(36)}${n}`, inMs, outMs, label: `Clip ${n}`, origin, score: null,
    reason: null };
}

export function toClipIn(d: Draft): ClipIn {
  const c: ClipIn = { t_mv_in_ms: d.inMs, t_mv_out_ms: d.outMs, label: d.label.trim() || null, origin: d.origin };
  if (d.score != null) c.score = d.score;
  if (d.reason) c.reason = d.reason.slice(0, 500);
  return c;
}

const storeKey = (videoId: number) => `ws8.drafts.${videoId}`;

export function loadDrafts(videoId: number): Draft[] {
  try {
    const raw = sessionStorage.getItem(storeKey(videoId));
    const v: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? (v as Draft[]).filter((d) => typeof d.inMs === "number" && typeof d.outMs === "number") : [];
  } catch {
    return [];
  }
}

export function saveDrafts(videoId: number, drafts: Draft[]): void {
  try {
    if (drafts.length) sessionStorage.setItem(storeKey(videoId), JSON.stringify(drafts));
    else sessionStorage.removeItem(storeKey(videoId));
  } catch {
    /* storage unavailable: drafts live in memory only */
  }
}

export type Pending = { kind: "clip"; key: string; clip: MvClipOut } | { kind: "suggestion"; key: string; s: Suggestion };

/** Proposed clip rows first, then computed suggestions not already a clip, drafted or rejected this session. */
export function pendingSuggestions(tl: Timeline, drafts: readonly Draft[], rejected: ReadonlySet<string>): Pending[] {
  const taken = new Set([...tl.clips.map((c) => winKey(c.t_mv_in_ms, c.t_mv_out_ms)), ...drafts.map((d) => d.key)]);
  const out: Pending[] = tl.clips.filter((c) => c.state === "proposed").map((c) => ({ kind: "clip", key: `c${c.id}`, clip: c }));
  for (const s of tl.suggestions) {
    const k = winKey(s.t_mv_in_ms, s.t_mv_out_ms);
    if (!taken.has(k) && !rejected.has(k)) out.push({ kind: "suggestion", key: k, s });
  }
  return out;
}

/** Move one edge of a window by ±1 frame or to the previous/next beat. */
export function stepEdge(w: Win, edge: "in" | "out", how: "frame" | "beat", dir: 1 | -1, fps: number,
  beats: readonly number[]): Win {
  const cur = edge === "in" ? w.inMs : w.outMs;
  const next = how === "frame" ? stepFrames(cur, fps, dir) : nextOnGrid(cur, beats, dir) ?? cur;
  return edge === "in" ? { ...w, inMs: next } : { ...w, outMs: next };
}

/** Cut status of a clip from the board's clip subitems (steps cell), else from its media. */
export function cutStatus(clip: MvClipOut, sub: BoardPage | undefined): { state: string; text: string } {
  const item = sub?.items.find((i) => i.id === clip.id);
  const steps = item?.cells.steps?.items ?? [];
  for (const s of steps) {
    const name = typeof s.step === "string" ? s.step : "";
    if (name.includes("cut")) {
      const state = typeof s.state === "string" ? s.state : "pending";
      return { state, text: `cut: ${state.replace("_", " ")}` };
    }
  }
  return clip.master_media_id != null ? { state: "done", text: "cut: done" } : { state: "pending", text: "cut: pending" };
}
